import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { Smartphone, WifiOff, Loader2 } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface Props {
  /** Live Twilio call SID of the browser leg (undefined while dialing). */
  callSid: string | undefined;
  callQuality: 'good' | 'fair' | 'poor' | null;
  className?: string;
}

type HandoffState = 'idle' | 'dialing' | 'moved' | 'failed';

/**
 * "Move call to my mobile": rings the user's verified mobile into the same
 * conference; when it answers, the server drops this browser leg. Shown only
 * when the server says a verified mobile is available. Poor network quality
 * (Twilio Device 'warning' events -> callQuality) surfaces it as a suggestion.
 */
export function MoveToMobileButton({ callSid, callQuality, className = '' }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<HandoffState>('idle');
  const twilioSid = callSid && /^CA[0-9a-f]{32}$/i.test(callSid) ? callSid : undefined;

  const { data } = useQuery<{ available: boolean; number?: string }>({
    queryKey: [`/api/voice/incall/${twilioSid}/handoff`],
    enabled: !!twilioSid,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!twilioSid || state !== 'dialing') return;
    let token: string | null = null;
    try { token = localStorage.getItem('token'); } catch { /* storage blocked */ }
    if (!token) return;
    const socket: Socket = io({ auth: { token }, reconnectionAttempts: 5, timeout: 10000 });
    socket.on('connect', () => socket.emit('auth', { token }));
    socket.on('call:handoff', (msg: any) => {
      if (!msg || msg.callSid !== twilioSid) return;
      if (msg.status === 'moved') {
        setState('moved');
        toast({ title: 'Call moved to your mobile' });
      } else if (msg.status === 'failed') {
        setState('failed');
        toast({
          title: 'Could not move the call',
          description: msg.reason === 'voicemail' ? 'Your mobile went to voicemail. You are still on the call here.' : 'Your mobile did not answer. You are still on the call here.',
          variant: 'destructive',
        });
      }
    });
    return () => { socket.disconnect(); };
  }, [twilioSid, state, toast]);

  if (!twilioSid || !data?.available || state === 'moved') return null;

  const start = async () => {
    setState('dialing');
    try {
      await apiRequest('POST', `/api/voice/incall/${encodeURIComponent(twilioSid)}/handoff`, {});
      toast({ title: `Ringing your mobile ${data.number || ''}`.trim(), description: 'Answer it and this browser call will hand over.' });
    } catch (e: any) {
      setState('failed');
      toast({ title: 'Could not move the call', description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  };

  const poor = callQuality === 'poor';
  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      {poor && state !== 'dialing' && (
        <p className="flex items-center gap-1 text-xs text-amber-600" role="status" data-testid="text-handoff-suggestion">
          <WifiOff className="h-3.5 w-3.5" aria-hidden /> Poor connection. Move this call to your mobile?
        </p>
      )}
      <button
        type="button"
        onClick={start}
        disabled={state === 'dialing'}
        data-testid="button-move-to-mobile"
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 ${poor ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200'}`}
      >
        {state === 'dialing' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Smartphone className="h-3.5 w-3.5" aria-hidden />}
        {state === 'dialing' ? 'Ringing your mobile…' : 'Move call to my mobile'}
      </button>
    </div>
  );
}
